import { ClassNames } from '@emotion/core'
import styled from '@emotion/styled/macro'
import { IRenderFunction } from '@uifabric/utilities'
import { TimeAgo } from 'components'
import {
  EntityList,
  EntityListProps,
  renderDefault,
} from 'components/EntityList'
import { isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { themeFont } from 'mixins'
import {
  ProvingChange,
  ProvingChangeOperation,
  ProvingChangeTracking,
  ProvingUserRole,
  UserRole,
} from 'model'
import {
  Callout,
  CommandBar,
  IColumn,
  IDetailsRowProps,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { Theme } from 'theme'
import {
  useAcceptProvingChangeMutation,
  useRejectProvingChangeMutation,
} from './hooks/ProvingChange.generated'

export type EntityChangeListProps<
  T extends { _id: string; _changes?: ProvingChangeTracking[] }
> = EntityListProps<T>

type ColumnRenderer = (
  item?: any,
  index?: number | undefined,
  column?: IColumn | undefined
) => any

const ColumnChangeBackground = styled.div`
  position: relative;
  background: #ffe000;
  cursor: default;
  overflow: hidden;
  height: 100%;
`

const CalloutContent = styled.div`
  min-width: 320px;
  max-width: 480px;
  padding: 0.5em 1em;
`
const CalloutTitle = styled.div`
  ${themeFont('large')};
  margin-top: 0.5em;
  margin-bottom: 0.66em;
`
const CalloutMeta = styled.div`
  margin-bottom: 1em;
`

export const ColumnChangeWrapper: React.FC<{
  change: ProvingChange
  original?: any
}> = ({ children, change, original }) => {
  const [proving, refetch] = useCurrentProving()
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [calloutState, setCalloutState] = React.useState({ hidden: true })
  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isProvingOrganisator = isInProvingRole(proving, ProvingUserRole.Editor)
  const isResourceManager = auth.hasUserRole(UserRole.ResourceManager)
  const isHRG = auth.hasUserRole(UserRole.Hrg)
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  const [accept] = useAcceptProvingChangeMutation({
    variables: { changeId: change._id },
  })
  const [reject] = useRejectProvingChangeMutation({
    variables: { changeId: change._id },
  })

  const toggleCallout = React.useCallback(
    () =>
      setCalloutState((state) => ({
        hidden: !state.hidden,
      })),
    [setCalloutState]
  )
  const hideCallout = React.useCallback(
    () =>
      setCalloutState({
        hidden: true,
      }),
    [setCalloutState]
  )

  React.useEffect(() => {
    const node = contentRef.current
    if (node && node.parentNode) {
      node.parentNode.addEventListener('click', toggleCallout)

      return () => {
        node.removeEventListener('click', toggleCallout)
      }
    }

    return () => void 0
  }, [contentRef, toggleCallout])

  return (
    <>
      {(isAdmin ||
        isResourceManager ||
        isHRG ||
        isServiceTeam ||
        isProvingOrganisator) && (
        <Callout
          gapSpace={0}
          target={contentRef.current}
          isBeakVisible={true}
          setInitialFocus={true}
          hidden={calloutState.hidden}
          onDismiss={hideCallout}
        >
          <CalloutContent>
            <CalloutTitle>Änderung</CalloutTitle>
            <CalloutMeta>
              erstellt <TimeAgo date={change.created!} /> von{' '}
              {change.createdBy.name}
              <br />
              Ursprünglicher Wert: <b>{original}</b>
            </CalloutMeta>
            <CommandBar
              items={[
                {
                  key: 'accept',
                  iconProps: {
                    iconName: 'CheckMark',
                    style: { color: 'green' },
                  },
                  text: 'Akzeptieren',
                  onClick: () => {
                    accept().then(refetch).then(hideCallout)
                  },
                },
                {
                  key: 'reject',
                  iconProps: { iconName: 'Cancel' },
                  style: { display: isHRG ? 'none' : 'flex' },
                  disabled: isHRG,
                  text: 'Ablehnen',
                  onClick: () => {
                    reject().then(refetch).then(hideCallout)
                  },
                },
              ]}
            />
          </CalloutContent>
        </Callout>
      )}

      <ColumnChangeBackground ref={contentRef}>
        {' '}
        {children}
      </ColumnChangeBackground>
    </>
  )
}
const RowChangeWrapper: React.FC<{
  change: ProvingChange
}> = ({ children, change }) => {
  const [proving, refetch] = useCurrentProving()
  const changeType = change.change.type
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [calloutState, setCalloutState] = React.useState({ hidden: true })
  const [accept] = useAcceptProvingChangeMutation({
    variables: { changeId: change._id },
  })
  const [reject] = useRejectProvingChangeMutation({
    variables: { changeId: change._id },
  })
  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isProvingOrganisator = isInProvingRole(proving, ProvingUserRole.Editor)
  const isResourceManager = auth.hasUserRole(UserRole.ResourceManager)
  const isHRG = auth.hasUserRole(UserRole.Hrg)
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  const toggleCallout = React.useCallback(
    () =>
      setCalloutState((state) => ({
        hidden: !state.hidden,
      })),
    [setCalloutState]
  )
  const hideCallout = React.useCallback(
    () =>
      setCalloutState({
        hidden: true,
      }),
    [setCalloutState]
  )

  return (
    <>
      {(isAdmin ||
        isResourceManager ||
        isHRG ||
        isServiceTeam ||
        isProvingOrganisator) && (
        <Callout
          gapSpace={0}
          target={
            contentRef.current
              ? contentRef.current.getElementsByClassName(
                  'ms-DetailsRow-check'
                )[0] || contentRef.current
              : null
          }
          isBeakVisible={true}
          setInitialFocus={true}
          hidden={calloutState.hidden}
          onDismiss={hideCallout}
        >
          <CalloutContent>
            <CalloutTitle>Änderung</CalloutTitle>
            <CalloutMeta>
              {changeType === ProvingChangeOperation.Add
                ? 'hinzugefügt'
                : 'gelöscht'}{' '}
              <TimeAgo date={change.created!} /> von {change.createdBy.name}
            </CalloutMeta>
            <CommandBar
              items={[
                {
                  key: 'accept',
                  iconProps: {
                    iconName: 'CheckMark',
                    style: { color: 'green' },
                  },
                  text: 'Akzeptieren',
                  onClick: () => {
                    accept().then(refetch).then(hideCallout)
                  },
                },
                {
                  key: 'reject',
                  style: { display: isHRG ? 'none' : 'flex' },
                  iconProps: { iconName: 'Cancel' },
                  disabled: isHRG,
                  text: 'Ablehnen',
                  onClick: () => {
                    reject().then(refetch).then(hideCallout)
                  },
                },
              ]}
            />
          </CalloutContent>
        </Callout>
      )}

      <div ref={contentRef} onClick={toggleCallout}>
        <ClassNames>
          {({ css, theme }) =>
            React.cloneElement(children as any, {
              className: css({
                background: (theme as Theme).palette[
                  changeType === ProvingChangeOperation.Add
                    ? 'greenLight'
                    : 'orangeLighter'
                ],
              }),
            })
          }
        </ClassNames>
      </div>
    </>
  )
}
const RowModifiedWrapper: React.FC = ({ children }) => (
  <ClassNames>
    {({ css, theme }) =>
      React.cloneElement(children as any, {
        className: css({
          background: (theme as Theme).palette.yellowLight,
        }),
      })
    }
  </ClassNames>
)

export const EntityChangeList = <
  T extends { _id: string; _changes?: ProvingChangeTracking[] }
>(
  props: EntityChangeListProps<T>
) => {
  const onRenderRow = props.onRenderRow
  const handleRenderRow: IRenderFunction<IDetailsRowProps> = React.useCallback(
    (rowProps, defaultRender) => {
      if (rowProps) {
        const row = onRenderRow
          ? onRenderRow(rowProps, defaultRender)
          : defaultRender!(rowProps)

        if (rowProps.item._changes && rowProps.item._changes.rootChange) {
          return (
            <RowChangeWrapper change={rowProps.item._changes.rootChange}>
              {row}
            </RowChangeWrapper>
          )
        }

        if (rowProps.item._changes && rowProps.item.$modified) {
          return <RowModifiedWrapper>{row}</RowModifiedWrapper>
        }

        return row
      }

      return null
    },
    [onRenderRow]
  )

  const createChangeColumnRenderer = React.useCallback(
    (columnRenderer: ColumnRenderer | undefined) => {
      const renderer = columnRenderer || renderDefault
      return (
        item: any,
        index: number | undefined,
        column: IColumn | undefined
      ) => {
        const changes = item._changes ? item._changes.changes : []
        const columnChange = changes.filter(
          ({ change: { relativePath, value, key } }: any) =>
            (relativePath && relativePath.startsWith(column!.fieldName)) ||
            // NOTE: because the track hotels column use index in name and this doesn't work with the new change tracking. But tracking hotels have sort index in the name, so we can use this to find the correct change
            (relativePath.includes('trackHotel') &&
              column!.fieldName!.match(/\[(\d+)\]/)?.[1] ===
                (value?.index - 1)?.toString()) ||
            // NOTE: this track hotel hack is only needed temporarily to stay compatible with the old track hotel array index tracking (11.2023)
            (relativePath.includes('trackHotel')
              ? relativePath &&
                relativePath.includes(column!.fieldName!.split('.')[1]) &&
                (value.index - 1).toString() ===
                  column!.fieldName!.split('.')[2]
              : relativePath &&
                relativePath.startsWith(column!.fieldName!.split('.')[0]))
          // END NOTE
        )[0]
        const oldItem = columnChange
          ? {
              ...item,
              [columnChange.change.key]:
                columnChange.change.type === ProvingChangeOperation.Remove
                  ? undefined
                  : columnChange.change.oldValue,
            }
          : item
        return !item._changes.rootChange && columnChange ? (
          <ColumnChangeWrapper
            change={columnChange}
            original={renderer(oldItem, index, column)}
          >
            {renderer(item, index, column)}
          </ColumnChangeWrapper>
        ) : (
          renderer(item, index, column)
        )
      }
    },
    []
  )

  const columns = React.useMemo(
    () =>
      props.columns!.map((column) => ({
        ...column,
        onRender: createChangeColumnRenderer(column.onRender),
      })),
    [props.columns, createChangeColumnRenderer]
  )

  return (
    <EntityList
      {...props}
      columns={columns}
      items={props.items}
      onRenderRow={handleRenderRow}
    />
  )
}

// export class EntityChangeList<
//   T extends { _id: string; changes?: ProvingChange[] } = any
// > extends React.Component<EntityChangeListProps<T>> {
//   public render() {
//     return (
//       <EntityList
//         {...this.props}
//         columns={this.props.columns!.map(column => ({
//           ...column,
//           onRender: this.createChangeColumnRenderer(column.onRender),
//         }))}
//         items={this.props.items}
//         onRenderRow={this.handleRenderRow}
//       />
//     )
//   }

//   private handleRenderRow: IRenderFunction<IDetailsRowProps> = (
//     rowProps,
//     defaultRender
//   ) => {
//     if (rowProps) {
//       const row = this.props.onRenderRow
//         ? this.props.onRenderRow(rowProps, defaultRender)
//         : defaultRender!(rowProps)

//       if (rowProps.item._changes && rowProps.item._changes.rootChange) {
//         return (
//           <RowChangeWrapper change={rowProps.item._changes.rootChange}>
//             {row}
//           </RowChangeWrapper>
//         )
//       }

//       if (rowProps.item._changes && rowProps.item.$modified) {
//         return <RowModifiedWrapper>{row}</RowModifiedWrapper>
//       }

//       return row
//     }

//     return null
//   }

//   private createChangeColumnRenderer: (
//     columnRenderer?: ColumnRenderer
//   ) => ColumnRenderer = columnRenderer => {
//     const renderer = columnRenderer || renderDefault
//     return (item, index, column) => {
//       const changes = item._changes ? item._changes.changes : []
//       const columnChange = changes.filter(
//         ({ change: { relativePath } }: any) =>
//           (relativePath && relativePath.startsWith(column!.fieldName!)) ||
//           (relativePath.includes('trackHotel')
//             ? relativePath &&
//               relativePath.includes(column!.fieldName!.split('.')[1])
//             : relativePath &&
//               relativePath.startsWith(column!.fieldName!.split('.')[0]))
//       )[0]
//       const oldItem = columnChange
//         ? {
//             ...item,
//             [columnChange.change.key]:
//               columnChange.change.type === ProvingChangeOperation.Remove
//                 ? undefined
//                 : columnChange.change.oldValue,
//           }
//         : item
//       return !item._changes.rootChange && columnChange ? (
//         <ColumnChangeWrapper
//           change={columnChange}
//           original={renderer(oldItem, index, column)}
//         >
//           {renderer(item, index, column)}
//         </ColumnChangeWrapper>
//       ) : (
//         renderer(item, index, column)
//       )
//     }
//   }
// }
